import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSettings, FiUser, FiMail, FiLock, FiEye, FiEyeOff, FiBell, FiGlobe, FiSun, FiMoon, FiSave, FiTrash2, FiLogOut } = FiIcons;

const Settings = () => {
  const { user, updateUser, logout } = useAuth();
  const { addNotification, clearAllNotifications, theme, fontSize, language, updateAppearance } = useApp();

  // Profile settings
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    country: user?.country || '',
    bio: user?.bio || ''
  });

  // Password settings
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Notification settings
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    studyReminders: true,
    groupMessages: true,
    marketingEmails: false
  });

  // Appearance settings
  const [appearanceSettings, setAppearanceSettings] = useState({
    theme: theme || 'light',
    fontSize: fontSize || 'medium',
    language: language || 'en'
  });

  // Update appearance settings when they change in the context
  useEffect(() => {
    setAppearanceSettings(prev => ({
      ...prev,
      theme,
      fontSize,
      language
    }));
    // Log current theme for debugging
    console.log('Current theme in Settings:', theme);
  }, [theme, fontSize, language]);

  // Active tab state
  const [activeTab, setActiveTab] = useState('profile');

  // Handle profile data changes
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle password data changes
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle notification settings changes
  const handleNotificationChange = (setting) => {
    setNotificationSettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };

  // Handle appearance settings changes
  const handleAppearanceChange = (setting, value) => {
    console.log(`Changing ${setting} to ${value}`);
    setAppearanceSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  // Save profile settings
  const saveProfileSettings = () => {
    updateUser(profileData);
    addNotification({
      type: 'success',
      title: 'Profile Updated',
      message: 'Your profile information has been updated successfully.'
    });
  };

  // Save password settings
  const savePasswordSettings = () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      addNotification({
        type: 'error',
        title: 'Password Mismatch',
        message: 'New password and confirmation do not match.'
      });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      addNotification({
        type: 'error',
        title: 'Password Too Short',
        message: 'Password must be at least 6 characters long.'
      });
      return;
    }

    // In a real app, this would call an API to change the password
    addNotification({
      type: 'success',
      title: 'Password Updated',
      message: 'Your password has been updated successfully.'
    });

    // Clear password fields
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  };

  // Save notification settings
  const saveNotificationSettings = () => {
    // In a real app, this would save to a database
    addNotification({
      type: 'success',
      title: 'Notification Settings Updated',
      message: 'Your notification preferences have been saved.'
    });
  };

  // Save appearance settings
  const saveAppearanceSettings = () => {
    // Log before saving
    console.log('Saving appearance settings:', appearanceSettings);
    
    // Update the app context with the new appearance settings
    updateAppearance(appearanceSettings);
    
    addNotification({
      type: 'success',
      title: 'Appearance Settings Updated',
      message: 'Your display preferences have been saved and applied.'
    });
  };

  // Handle account deletion
  const handleDeleteAccount = () => {
    const confirmDelete = window.confirm(
      'Are you sure you want to delete your account? This action cannot be undone.'
    );

    if (confirmDelete) {
      // In a real app, this would call an API to delete the account
      addNotification({
        type: 'info',
        title: 'Account Deletion Requested',
        message: 'Your account deletion request has been submitted. You will receive an email with further instructions.'
      });
    }
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    window.location.href = '/#/login';
  };

  return (
    <div className="space-y-6 md:space-y-8 pb-16 md:pb-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
      >
        <div className="text-center">
          <SafeIcon icon={FiSettings} className="w-16 h-16 mx-auto mb-4 text-primary-600" />
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Settings</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Customize your ILM Global experience and manage your account preferences.
          </p>
        </div>
      </motion.div>

      {/* Tabs & Content */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Tabs - Vertical on desktop, horizontal on mobile */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-xl shadow-lg border border-gray-100 p-4 md:p-0 overflow-x-auto md:overflow-visible"
        >
          <div className="flex md:flex-col md:h-full whitespace-nowrap md:whitespace-normal">
            {[
              { id: 'profile', label: 'Profile Information', icon: FiUser },
              { id: 'password', label: 'Password & Security', icon: FiLock },
              { id: 'notifications', label: 'Notifications', icon: FiBell },
              { id: 'appearance', label: 'Appearance', icon: FiSun }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-4 py-3 md:py-4 rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-primary-50 text-primary-600 font-medium'
                    : 'text-gray-600 hover:bg-gray-50'
                } mr-2 md:mr-0 md:mb-2`}
              >
                <SafeIcon icon={tab.icon} className="w-5 h-5 mr-3" />
                <span>{tab.label}</span>
              </button>
            ))}

            <div className="md:mt-auto">
              <button
                onClick={handleLogout}
                className="flex items-center px-4 py-3 md:py-4 text-red-600 hover:bg-red-50 rounded-lg transition-colors mr-2 md:mr-0 md:mt-4"
              >
                <SafeIcon icon={FiLogOut} className="w-5 h-5 mr-3" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Content Panel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 md:col-span-3"
        >
          {/* Profile Settings */}
          {activeTab === 'profile' && (
            <div>
              <h2 className="text-xl font-bold text-gray-800 mb-6">Profile Information</h2>

              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={profileData.name}
                    onChange={handleProfileChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={profileData.email}
                      onChange={handleProfileChange}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                    Country
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiGlobe} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <select
                      id="country"
                      name="country"
                      value={profileData.country}
                      onChange={handleProfileChange}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Select Country</option>
                      <option value="Sierra Leone">Sierra Leone</option>
                      <option value="The Gambia">The Gambia</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-1">
                    Bio
                  </label>
                  <textarea
                    id="bio"
                    name="bio"
                    rows="4"
                    value={profileData.bio}
                    onChange={handleProfileChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Tell us about yourself..."
                  ></textarea>
                </div>

                <div className="flex justify-between pt-4 border-t border-gray-200">
                  <button
                    onClick={handleDeleteAccount}
                    className="text-red-600 hover:text-red-700 text-sm font-medium"
                  >
                    Delete Account
                  </button>

                  <button
                    onClick={saveProfileSettings}
                    className="flex items-center bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors"
                  >
                    <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Password Settings */}
          {activeTab === 'password' && (
            <div>
              <h2 className="text-xl font-bold text-gray-800 mb-6">Password & Security</h2>

              <div className="space-y-6">
                <div>
                  <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Current Password
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      id="currentPassword"
                      name="currentPassword"
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                      className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <SafeIcon icon={showCurrentPassword ? FiEyeOff : FiEye} className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      id="newPassword"
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <SafeIcon icon={showNewPassword ? FiEyeOff : FiEye} className="w-5 h-5" />
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Password must be at least 6 characters long
                  </p>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <SafeIcon icon={showConfirmPassword ? FiEyeOff : FiEye} className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={savePasswordSettings}
                    className="flex items-center bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors"
                  >
                    <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                    Update Password
                  </button>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
                  <h3 className="text-sm font-medium text-yellow-800 mb-2">Security Tips</h3>
                  <ul className="text-xs text-yellow-700 space-y-1">
                    <li>• Use a unique password that you don't use elsewhere</li>
                    <li>• Include a mix of letters, numbers, and symbols</li>
                    <li>• Avoid using personal information in your password</li>
                    <li>• Consider using a password manager for better security</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Notification Settings */}
          {activeTab === 'notifications' && (
            <div>
              <h2 className="text-xl font-bold text-gray-800 mb-6">Notification Settings</h2>

              <div className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-gray-800">Email Notifications</h3>
                      <p className="text-xs text-gray-500">Receive updates and reminders via email</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={notificationSettings.emailNotifications}
                        onChange={() => handleNotificationChange('emailNotifications')}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-gray-800">Push Notifications</h3>
                      <p className="text-xs text-gray-500">Receive notifications in the app</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={notificationSettings.pushNotifications}
                        onChange={() => handleNotificationChange('pushNotifications')}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-gray-800">Study Reminders</h3>
                      <p className="text-xs text-gray-500">Receive reminders for quizzes and study sessions</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={notificationSettings.studyReminders}
                        onChange={() => handleNotificationChange('studyReminders')}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-gray-800">Group Messages</h3>
                      <p className="text-xs text-gray-500">Receive notifications for new messages in your study groups</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={notificationSettings.groupMessages}
                        onChange={() => handleNotificationChange('groupMessages')}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-gray-800">Marketing Emails</h3>
                      <p className="text-xs text-gray-500">Receive updates about new features and opportunities</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={notificationSettings.marketingEmails}
                        onChange={() => handleNotificationChange('marketingEmails')}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
                    </label>
                  </div>
                </div>

                <div className="flex justify-between pt-4 border-t border-gray-200">
                  <button
                    onClick={clearAllNotifications}
                    className="text-gray-600 hover:text-gray-700 text-sm font-medium"
                  >
                    Clear All Notifications
                  </button>

                  <button
                    onClick={saveNotificationSettings}
                    className="flex items-center bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors"
                  >
                    <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                    Save Preferences
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Appearance Settings */}
          {activeTab === 'appearance' && (
            <div>
              <h2 className="text-xl font-bold text-gray-800 mb-6">Appearance Settings</h2>

              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-800 mb-3">Theme</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div
                      onClick={() => handleAppearanceChange('theme', 'light')}
                      className={`p-4 border rounded-lg cursor-pointer ${
                        appearanceSettings.theme === 'light'
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center mb-3">
                        <SafeIcon icon={FiSun} className="w-5 h-5 text-orange-500 mr-2" />
                        <span className="font-medium">Light Mode</span>
                      </div>
                      <div className="h-20 bg-white border border-gray-200 rounded"></div>
                    </div>

                    <div
                      onClick={() => handleAppearanceChange('theme', 'dark')}
                      className={`p-4 border rounded-lg cursor-pointer ${
                        appearanceSettings.theme === 'dark'
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center mb-3">
                        <SafeIcon icon={FiMoon} className="w-5 h-5 text-blue-500 mr-2" />
                        <span className="font-medium">Dark Mode</span>
                      </div>
                      <div className="h-20 bg-gray-800 border border-gray-700 rounded"></div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-800 mb-3">Font Size</h3>
                  <div className="grid grid-cols-3 gap-4">
                    {['small', 'medium', 'large'].map((size) => (
                      <button
                        key={size}
                        onClick={() => handleAppearanceChange('fontSize', size)}
                        className={`py-2 px-4 border rounded-lg ${
                          appearanceSettings.fontSize === size
                            ? 'border-primary-500 bg-primary-50 text-primary-600'
                            : 'border-gray-200 hover:border-gray-300 text-gray-700'
                        }`}
                      >
                        <span
                          className={
                            size === 'small'
                              ? 'text-sm'
                              : size === 'medium'
                                ? 'text-base'
                                : 'text-lg'
                          }
                        >
                          {size.charAt(0).toUpperCase() + size.slice(1)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-800 mb-3">Language</h3>
                  <select
                    value={appearanceSettings.language}
                    onChange={(e) => handleAppearanceChange('language', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="en">English</option>
                    <option value="fr">French</option>
                    <option value="ar">Arabic</option>
                    <option value="es">Spanish</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Note: Not all languages are fully supported yet
                  </p>
                </div>

                <div className="flex justify-end pt-4 border-t border-gray-200">
                  <button
                    onClick={saveAppearanceSettings}
                    className="flex items-center bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors"
                  >
                    <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                    Save Preferences
                  </button>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Settings;